/**
 * SupplierFormFields Component
 * Delade formulärfält för Add/Update Supplier
 */

import React from 'react';
import FormInput from '../../../components/common/FormInput';

export const SupplierFormFields = ({
  values,
  discounts,
  previewUrl,
  errorMessage,
  handleChange,
  handleAvatarChange,
  handleRemoveAvatar,
  addDiscount,
  updateDiscount,
  removeDiscount,
  isUpdate,
  supplierUsers = []
}) => {
  return (
    <>
      {/* Grundläggande information */}
      <FormInput
        label="Företagsnamn"
        name="companyName"
        value={values.companyName}
        onChange={handleChange}
        required
        placeholder="Ange företagsnamn"
        error={errorMessage && !values.companyName ? 'Företagsnamn krävs' : ''}
      />

      <FormInput
        label="Leverantörstyp"
        name="supplierType"
        value={values.supplierType}
        onChange={handleChange}
        required
        placeholder="Ange leverantörstyp"
        error={errorMessage && !values.supplierType ? 'Leverantörstyp krävs' : ''}
      />

      <FormInput
        label="Hemsida"
        type="url"
        name="website"
        value={values.website}
        onChange={handleChange}
        placeholder="https://exempel.se"
      />

      {/* Huvudkontakt (endast vid uppdatering) */}
      {isUpdate && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Huvudkontakt</label>
          <select
            name="primaryContactPerson"
            value={values.primaryContactPerson}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Välj huvudkontakt</option>
            {supplierUsers.map(user => (
              <option key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {supplierUsers.length === 0
              ? "Ingen användare med rollen Materialleverantör finns för detta företag — fyll i kontaktuppgifter nedan i stället"
              : "Välj vem som ska vara huvudkontakt för denna leverantör"}
          </p>
        </div>
      )}

      {/* Kontaktuppgifter utan användarkonto.
          De flesta leverantörer har en kontaktperson som användare i systemet,
          men långt ifrån alla. Tidigare tvingade formuläret fram ett konto för
          varje kontakt, vilket gav halvfejkade användare vars enda syfte var
          att bära en mailadress. Fälten har funnits i modellen och backend hela
          tiden — de exponerades bara aldrig. */}
      <div className="space-y-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Kontaktuppgifter</h4>
          <p className="text-xs text-gray-500">
            Används när leverantören saknar användarkonto. IA ser dem när kunden
            vill bli kopplad.
          </p>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Kontaktperson</label>
          <input
            type="text"
            name="contactPerson"
            value={values.contactPerson || ''}
            onChange={handleChange}
            placeholder="För- och efternamn"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {/* Namnet är frivilligt. Har vi bara en mailadress används den som
              kontakt — det räcker för att ta kontakt, och är ofta allt vi har
              för mindre leverantörer. */}
          {!values.contactPerson && values.email && (
            <p className="text-xs text-gray-500">
              Inget namn angivet — <span className="font-medium">{values.email}</span> visas
              som kontaktperson.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">E-post</label>
          <input
            type="email"
            name="email"
            value={values.email || ''}
            onChange={handleChange}
            placeholder="kontakt@leverantor.se"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Telefon <span className="text-gray-400 font-normal">(valfritt)</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={values.phone || ''}
            onChange={handleChange}
            placeholder="070-123 45 67"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Kontaktmetod */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Kontaktmetod</label>
        <div className="space-y-2">
          <div className="flex items-center">
            <input
              id="direct-contact"
              name="contactMethod"
              type="radio"
              value="direct"
              checked={values.contactMethod === 'direct'}
              onChange={handleChange}
              className="h-4 w-4 text-leadme-pink focus:ring-leadme-pink-light border-gray-300"
            />
            <label htmlFor="direct-contact" className="ml-2 block text-sm text-gray-700">
              Direktkontakt via e-post med leverantören
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              id="leadme-contact"
              name="contactMethod"
              type="radio"
              value="leadme"
              checked={values.contactMethod === 'leadme'}
              onChange={handleChange}
              className="h-4 w-4 text-leadme-pink focus:ring-leadme-pink-light border-gray-300"
            />
            <label htmlFor="leadme-contact" className="ml-2 block text-sm text-gray-700">
              Kontakt via Leadme (går till projektets inredningsarkitekt)
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              id="affiliate-contact"
              name="contactMethod"
              type="radio"
              value="affiliate"
              checked={values.contactMethod === 'affiliate'}
              onChange={handleChange}
              className="h-4 w-4 text-leadme-pink focus:ring-leadme-pink-light border-gray-300"
            />
            <label htmlFor="affiliate-contact" className="ml-2 block text-sm text-gray-700">
              Direktlänk till leverantörens hemsida
            </label>
          </div>
        </div>
      </div>

      {values.contactMethod === 'affiliate' && (
        <FormInput
          label="Affiliate URL"
          name="affiliateUrl"
          type="url"
          value={values.affiliateUrl}
          onChange={handleChange}
          required
          placeholder="https://exempel.se/affiliate"
          error={errorMessage && !values.affiliateUrl ? 'URL krävs för direktlänk' : ''}
        />
      )}

      {/* Generell rabatt */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Generell rabatt</label>
        <div className="flex gap-4">
          <div className="w-1/4">
            <div className="relative">
              <input
                type="number"
                name="discountRate"
                value={values.discountRate}
                onChange={handleChange}
                placeholder="Rabatt %"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">%</span>
            </div>
          </div>
          <div className="flex-1">
            <input
              type="text"
              name="discountDescription"
              value={values.discountDescription}
              onChange={handleChange}
              placeholder="Rabattbeskrivning (t.ex. på kakel och klinker)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
            />
          </div>
        </div>
      </div>
      
      {/* Specifika rabatter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">Specifika rabatter</label>
          <button
            type="button"
            onClick={addDiscount}
            className="inline-flex items-center px-2 py-1 bg-leadme-pink-light text-white text-xs font-medium rounded hover:bg-leadme-pink transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Lägg till rabatt
          </button>
        </div>
        
        {discounts.length > 0 && (
          <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            {discounts.map((discount, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    value={discount.category}
                    onChange={(e) => updateDiscount(index, 'category', e.target.value)}
                    placeholder="Kategori (t.ex. Inredning)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  />
                </div>
                <div className="w-24">
                  <div className="relative">
                    <input
                      type="number"
                      value={discount.rate}
                      onChange={(e) => updateDiscount(index, 'rate', e.target.value)}
                      placeholder="Rabatt %"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                    />
                    <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">%</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeDiscount(index)}
                  className="p-2 text-leadme-red hover:text-leadme-red-dark hover:bg-leadme-red-light/20 rounded-full transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        
        <p className="text-xs text-gray-500 mt-1">
          Lägg till specifika rabatter för olika produktkategorier.
        </p>
      </div>

      {/* Synlighetsinställningar */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Synlighet</label>
        <div className="flex items-center gap-2">
          <input
            id="showInOffers"
            name="showInOffers"
            type="checkbox"
            checked={values.showInOffers !== false}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink-light"
          />
          <label htmlFor="showInOffers" className="text-sm text-gray-700">
            Visa i Erbjudanden (kundportalen)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="showInBrief"
            name="showInBrief"
            type="checkbox"
            checked={values.showInBrief !== false}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink-light"
          />
          <label htmlFor="showInBrief" className="text-sm text-gray-700">
            Visa i Brief (IA:s leverantörslista)
          </label>
        </div>
      </div>

      {/* Avatar */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Logotyp / Avatar</label>
        <div className="flex items-center space-x-4">
          <div className="relative w-24 h-12">
            {previewUrl && (
              <>
                <img
                  src={previewUrl}
                  alt="Avatar preview"
                  className="w-24 h-12 object-contain rounded border"
                />
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -top-2 -right-2 bg-leadme-red text-white rounded-full p-1 hover:bg-leadme-red-dark"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              id="avatar-upload"
              onChange={e => {
                const file = e.target.files[0];
                if (file) handleAvatarChange(file);
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-leadme-pink-light file:text-white hover:file:bg-leadme-pink"
              aria-label="Ladda upp logotyp eller avatar"
              tabIndex={0}
            />
          </div>
        </div>
      </div>
    </>
  );
};

